import { forwardRef, useEffect, useState } from 'react';
import { getDesignSystemIcon, getDesignSystemImage } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixIcon } from '@phoenix/design-system/frontend/ui/phoenix-icon/phoenix-icon';
import { Button } from '@phoenix/design-system/frontend/ui/button/button';

import { isAndroid, isIphone } from '../../util/device-detection/device-detection';
import { parseDate } from '../../util/format/dates';

const DAYS_TO_SHOW_AGAIN = 30;

interface SmartAppBannerProps {
  appName: string;
  appDescription: string;
  buttonText: string;
  isEnabled?: boolean;
}

export const SmartAppBanner = forwardRef<HTMLDivElement, SmartAppBannerProps>(
  ({ appName, appDescription, buttonText, isEnabled = false }, ref) => {
    const _isIphone = isIphone();
    const _isAndroid = isAndroid();
    const onMobileDevice = _isIphone || _isAndroid;

    const appIcon = getDesignSystemImage(process.env.NEXT_PUBLIC_APP_ICON_SRC || '');
    const appleStoreUrl = process.env.NEXT_PUBLIC_APP_APPLE_STORE_URL;
    const googleStoreUrl = process.env.NEXT_PUBLIC_APP_GOOGLE_STORE_URL;

    const cookieDismissedDate = document.cookie
      .split(';')
      .find((item) => item.trim().startsWith('SmartBannerClosed='))
      ?.split('=')[1];

    const [dismissed, setDismissed] = useState(!!cookieDismissedDate);

    useEffect(() => {
      if (cookieDismissedDate) {
        const today = new Date().getDate();
        const dismissedDate = parseDate(cookieDismissedDate).getDate();
        const dismissedOneMonthAgo = today >= dismissedDate + DAYS_TO_SHOW_AGAIN;

        if (dismissedOneMonthAgo) {
          setDismissed(false);
        }
      }
    }, []);

    const onDismiss = () => {
      const date = new Date();
      document.cookie = `SmartBannerClosed=${date.toISOString()}`;
      setDismissed(true);
    };

    const onView = () => window.open(_isIphone ? appleStoreUrl : googleStoreUrl);

    if ((_isIphone && !appleStoreUrl) || (_isAndroid && !googleStoreUrl)) return null;

    return isEnabled && onMobileDevice && !dismissed ? (
      <div className="bg-secondary-dark p-4 text-primary-white" ref={ref}>
        <div className="flex items-center space-x-4">
          <button onClick={onDismiss}>
            <PhoenixIcon icon={getDesignSystemIcon('icon-cross-16.svg')} />
          </button>
          <img className="h-[5rem] rounded-xl" src={appIcon} alt="White Stihl logo on an orange background" />
          <div className="flex flex-col">
            <span className="stihl-text-h4-mobile">{appName}</span>
            <span className="stihl-text-body-sm-reg">{appDescription}</span>
          </div>
        </div>
        <Button className="mt-4 w-full" btnType="white_outline_opaque" size="small" onClick={onView}>
          {buttonText}
        </Button>
      </div>
    ) : null;
  }
);
